// Edge Bézier cúbico horizontal (o "S" do n8n) entre handle-dir da origem e handle-esq do destino.

import type { FlowEdgeModel, FlowNodeModel } from './layout';

// d de um Bézier cúbico horizontal entre dois pontos.
export function edgePath(
  source: { x: number; y: number },
  target: { x: number; y: number },
): string {
  const dx = target.x - source.x;
  const c = Math.max(40, Math.abs(dx) * 0.5);
  return `M ${source.x},${source.y} C ${source.x + c},${source.y} ${target.x - c},${target.y} ${target.x},${target.y}`;
}

// Ponto do handle de saída (borda direita, centro-vertical) de um nó.
export function rightHandle(node: FlowNodeModel, pos: { x: number; y: number }) {
  return { x: pos.x + node.w, y: pos.y + node.h / 2 };
}

// Ponto do handle de entrada (borda esquerda, centro-vertical) de um nó.
export function leftHandle(node: FlowNodeModel, pos: { x: number; y: number }) {
  return { x: pos.x, y: pos.y + node.h / 2 };
}

// Resolve cor/espessura/opacidade de uma edge a partir do tipo e do estado de realce.
// A seta (marker-end) acompanha a mesma cor/opacidade — daí o id por variante.
// Realce: a edge mantém a cor do nó conectado (sua chart-N), só ganha ênfase
// (opacidade cheia + traço mais grosso) — não vira tudo "primary/amarelo".
export function edgeStyle(
  edge: FlowEdgeModel,
  highlighted: boolean,
): { stroke: string; width: number; opacity: number; markerId: string } {
  const ci = edge.colorIndex ?? 1;
  const stroke = `var(--color-chart-${ci})`;

  if (edge.kind === 'team-agent') {
    // team→agente herda a cor do agente, opacidade cheia.
    return {
      stroke,
      width: highlighted ? 2.5 : 1.5,
      opacity: 1,
      markerId: `arrow-c${ci}`,
    };
  }
  // agente→tool: cor do dono, mais sutil — ao realçar, sobe pra opacidade cheia.
  return highlighted
    ? { stroke, width: 2, opacity: 1, markerId: `arrow-c${ci}` }
    : { stroke, width: 1, opacity: 0.6, markerId: `arrow-c${ci}-soft` };
}

export function FlowEdge({
  edge,
  source,
  target,
  highlighted,
}: {
  edge: FlowEdgeModel;
  source: { x: number; y: number };
  target: { x: number; y: number };
  highlighted: boolean;
}) {
  const d = edgePath(source, target);
  const { stroke, width, opacity, markerId } = edgeStyle(edge, highlighted);

  return (
    <path
      d={d}
      stroke={stroke}
      stroke-width={width}
      fill="none"
      opacity={opacity}
      marker-end={`url(#${markerId})`}
    />
  );
}

// Definições de marcadores (arrowheads) referenciados via marker-end.
// Um por cor chart-N: variante cheia (opacidade 1) e atenuada (0.6, agente→tool
// em repouso). A seta acompanha a cor da edge, deixando clara a direção
// supervisor→agente→tool. `orient` alinha a ponta à tangente do path no destino.
const CHART_INDICES = [1, 2, 3, 4, 5] as const;

function Arrow({ id, fill, opacity }: { id: string; fill: string; opacity: number }) {
  return (
    <marker
      id={id}
      viewBox="0 0 10 10"
      refX="9"
      refY="5"
      markerWidth="7"
      markerHeight="7"
      orient="auto-start-reverse"
    >
      <path d="M 1 1 L 9 5 L 1 9 z" fill={fill} opacity={opacity} />
    </marker>
  );
}

export function EdgeMarkers() {
  return (
    <defs>
      {CHART_INDICES.map((i) => (
        <Arrow key={`c${i}`} id={`arrow-c${i}`} fill={`var(--color-chart-${i})`} opacity={1} />
      ))}
      {CHART_INDICES.map((i) => (
        <Arrow
          key={`c${i}-soft`}
          id={`arrow-c${i}-soft`}
          fill={`var(--color-chart-${i})`}
          opacity={0.6}
        />
      ))}
    </defs>
  );
}
